import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "next-themes";
import { Sun, Moon, Menu, X } from "lucide-react";
import Logo from "./Logo";

const navItems = [
  { label: "Browse", target: "tools" },
  { label: "Quick Match", target: "finder" },
  { label: "Compare", target: "tools" },
  { label: "News", target: "news" },
  { label: "Newsletter", target: "newsletter" },
];

const Header = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    if (!isHome) { setIsVisible(true); return; }
    const handleScroll = () => setIsVisible(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHome]);

  const handleNav = (target: string) => {
    setMenuOpen(false);
    if (target === "news") {
      navigate("/news");
      return;
    }
    if (location.pathname !== "/") {
      navigate(`/#${target}`);
    } else {
      document.getElementById(target)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.header
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="fixed top-0 left-0 right-0 z-50 border-b border-border/60 bg-background/80 backdrop-blur-xl backdrop-saturate-150"
        >
          <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-1.5 shrink-0">
              <Logo size="xs" />
            </Link>

            {/* Desktop nav */}
            <nav className="hidden sm:flex items-center gap-0.5 ml-auto mr-12">
              {navItems.map((item) => (
                <button
                  key={item.label}
                  onClick={() => handleNav(item.target)}
                  className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-secondary/80 min-h-[36px] flex items-center"
                >
                  {item.label}
                </button>
              ))}
              <a
                href="mailto:hello@stackly.tools"
                className="text-[13px] font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2 rounded-lg hover:bg-secondary/80 min-h-[36px] flex items-center"
              >
                Submit a Tool
              </a>
            </nav>

            {/* Mobile hamburger */}
            <div className="sm:hidden flex items-center gap-1 mr-12">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="p-2.5 rounded-lg text-muted-foreground hover:text-foreground min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Menu"
              >
                {menuOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>

          {/* Mobile menu */}
          <AnimatePresence>
            {menuOpen && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="sm:hidden border-t border-border/60 bg-background/95 backdrop-blur-xl overflow-hidden"
              >
                <div className="px-4 py-3 flex flex-col gap-0.5">
                  {navItems.map((item) => (
                    <button
                      key={item.label}
                      onClick={() => handleNav(item.target)}
                      className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2.5 rounded-lg hover:bg-secondary text-left min-h-[44px]"
                    >
                      {item.label}
                    </button>
                  ))}
                  <a
                    href="mailto:hello@stackly.tools"
                    className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors px-3 py-2.5 rounded-lg hover:bg-secondary min-h-[44px] flex items-center"
                    onClick={() => setMenuOpen(false)}
                  >
                    Submit a Tool
                  </a>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.header>
      )}
    </AnimatePresence>
  );
};

export default Header;
